/**
 * Conditions: the one model of the table filter, the book filter and the
 * banner's condition columns. A condition is a list of groups that must all
 * hold. A plain group holds when any of its terms holds, a "none of" group
 * when none of them does. A term is an answer (its yes or no leaf is true)
 * or a number in a range, both bounds inclusive. A stored `string[][]`
 * filter is a condition as it is, and gives the script it always gave.
 *
 * Missing values follow the engine's three-valued logic and nothing here
 * fills a blank: a respondent with a missing value is outside a condition
 * and outside its negation ("NOT missing" is missing). Every path reaches
 * the script through `ref`.
 */
import { inLoadedData, plainNumber, ref, unsafeRef, unsafeText } from "../compiler/names"
import {
  Catalogue,
  CatalogueQuestion,
  findInstance,
  findQuestion,
  QuestionType,
} from "../types/catalogue"
import {
  Condition,
  ConditionGroup,
  ConditionTerm,
  isNoneGroup,
  isNumberTerm,
  NoneGroup,
  NumberTerm,
} from "../types/tabSpecDocument"
import { RAW_NAMES, ShownNames } from "./shownLabels"

// The types live with the document; re-exported for the compiler and the screens.
export { isNoneGroup, isNumberTerm }
export type { Condition, ConditionGroup, ConditionTerm, NoneGroup, NumberTerm }

/** The terms of a group, whatever its kind. */
export const groupTerms = (g: ConditionGroup): ConditionTerm[] => (isNoneGroup(g) ? g.none : g)

/** The codebook path a term names. */
export const termPath = (t: ConditionTerm): string => (isNumberTerm(t) ? t.path : t)

/** A blank path names nothing and is ignored, as it always was. */
const isBlank = (t: ConditionTerm): boolean => !termPath(t).trim()

/** Every path the condition names, each once, in order. A blank one is left out. */
export const conditionPaths = (c: Condition): string[] => [
  ...new Set(
    c
      .flatMap(groupTerms)
      .filter((t) => !isBlank(t))
      .map(termPath)
  ),
]

/**
 * A number field between its bounds, both inclusive: `(<<x>> >= 10 AND
 * <<x>> <= 20)`, one side when a bound is absent. With neither, everyone
 * with a value (a comparison with a missing value is missing). The bounds
 * are written as plain numbers (`plainNumber`): the callers refuse a bound
 * that cannot be written so before they get here.
 */
export const rangeExpression = (path: string, min?: number, max?: number): string => {
  const x = ref(path)
  const plain = (n: number): string => plainNumber(n) ?? String(n)
  const sides = [
    ...(min !== undefined ? [`${x} >= ${plain(min)}`] : []),
    ...(max !== undefined ? [`${x} <= ${plain(max)}`] : []),
  ]
  return `(${sides.length > 0 ? sides.join(" AND ") : `${x} == ${x}`})`
}

/** Whether a bound cannot be written into a script as a plain number (`plainNumber`). */
const unwritable = (n: number | undefined): boolean => n !== undefined && plainNumber(n) === null

/**
 * Why the bounds of a number term or a range cannot be used, as the end of a
 * sentence ("has no from or to value"), or null when they can: at least one
 * bound, each one a script can write as a plain number, the from value no
 * greater than the to value.
 */
export const boundsProblem = (min?: number, max?: number): string | null => {
  if (min === undefined && max === undefined) return "has no from or to value"
  if (unwritable(min) || unwritable(max)) return "has a from or to value the engine cannot take"
  if (min !== undefined && max !== undefined && min > max) return "has a from value above its to value"
  return null
}

/**
 * The terms of a group that reach the script: not blank, and no character
 * the script cannot carry. Leaving one out changes who is counted, so the
 * compiler refuses such a condition (`conditionRefusal`) rather than run
 * what is left.
 */
const usable = (terms: ConditionTerm[]): ConditionTerm[] =>
  terms.filter((t) => !isBlank(t) && !unsafeText(termPath(t)))

const termExpression = (t: ConditionTerm): string =>
  isNumberTerm(t) ? rangeExpression(t.path, t.min, t.max) : ref(t)

/**
 * The condition as a CALC expression; null when it holds for everyone. A
 * plain group is `(t1 OR t2)`, a "none of" group `NOT (t1 OR t2)`, the groups
 * joined with ` AND `. A `string[][]` gives exactly the script the table
 * filter always gave.
 */
export const conditionExpression = (c: Condition): string | null => {
  const parts = c.flatMap((g) => {
    const terms = usable(groupTerms(g))
    if (terms.length === 0) return []
    const any = `(${terms.map(termExpression).join(" OR ")})`
    return [isNoneGroup(g) ? `NOT ${any}` : any]
  })
  return parts.length > 0 ? parts.join(" AND ") : null
}

/** The question a path starts with: the longest start of it that names a question or a variable of the data. */
const questionOf = (catalogue: Catalogue, parts: string[]): CatalogueQuestion | null => {
  for (let i = parts.length; i >= 1; i--) {
    const start = parts.slice(0, i).join("//")
    const q = findQuestion(catalogue, start) ?? findQuestion(catalogue, start, "data")
    if (q) return q
  }
  return null
}

/**
 * A path in the words the condition editor shows: the question (a variable
 * nested in the data by its parts, "Brand: Bank 1"), then the grid row or
 * loop iteration after " - " ("Brand ratings - Brand B"), each as the tables
 * show it; and, for an answer (`answer`), the answer ("Agree"). `last` is
 * null for a number field and for a lone yes or no field. The catalogue
 * tells where the question's label ends; without it, or for a path it does
 * not know, `ShownNames.pathParts` decides.
 */
const pathWords = (
  path: string,
  answer: boolean,
  names: ShownNames,
  catalogue?: Catalogue
): { head: string; last: string | null } => {
  const parts = path.split("//")
  const q = catalogue ? questionOf(catalogue, parts) : null
  const v = q?.label ?? names.pathParts(path).variable
  const rest = parts.slice(v.split("//").length)
  const between = answer ? rest.slice(0, -1) : rest
  const shown = names.variable(v)
  const variable = shown === v ? v.split("//").join(": ") : shown
  const id = between.join("//")
  const own = q ? findInstance(q, id)?.label : undefined
  const instance = between.length > 0 ? names.instance(v, id, own || between.join(" - ")) : null
  return {
    head: instance === null ? variable : `${variable} - ${instance}`,
    last: answer && rest.length > 0 ? names.option(v, rest[rest.length - 1]) : null,
  }
}

/** "Spend from 10 to 20", "Spend 10 or more", "Spend up to 20"; the bounds as entered. */
const numberWords = (t: NumberTerm, names: ShownNames, catalogue?: Catalogue): string => {
  const field = pathWords(t.path, false, names, catalogue).head
  if (t.min !== undefined && t.max !== undefined) return `${field} from ${t.min} to ${t.max}`
  if (t.min !== undefined) return `${field} ${t.min} or more`
  if (t.max !== undefined) return `${field} up to ${t.max}`
  return field
}

/**
 * Each term of a group in words, in order, the answers of one question (or
 * one grid row) together, its name said once: "Gender: Male", "Female".
 */
const termWords = (terms: ConditionTerm[], names: ShownNames, catalogue?: Catalogue): string[] => {
  const entries: { head: string | null; words: string[] }[] = []
  terms.forEach((t) => {
    if (isNumberTerm(t)) {
      entries.push({ head: null, words: [numberWords(t, names, catalogue)] })
      return
    }
    const { head, last } = pathWords(t, true, names, catalogue)
    if (last === null) {
      entries.push({ head: null, words: [head] })
      return
    }
    const same = entries.find((e) => e.head === head)
    if (same) same.words.push(last)
    else entries.push({ head, words: [`${head}: ${last}`] })
  })
  return entries.flatMap((e) => e.words)
}

/** "a", "a or b", "a, b or c". */
const orList = (items: string[]): string =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`

/**
 * The condition in words, each path as the tables show it (`names`) and as
 * the condition editor names it, a grid row or loop iteration after " - "
 * ("Brand ratings - Brand B: Agree"): a group's terms joined with "or"
 * ("Gender: Male or Female"), a "none of" group as "not Gate: Yes" or "none
 * of Gate: Yes, No or Maybe", the groups joined with "and", a group of
 * several terms in brackets when there are several groups. Only the terms
 * that reach the script are described. null when the condition holds for
 * everyone. `catalogue`, when given, tells where a question's label ends
 * in a path, so a variable nested in the data reads by its parts.
 */
export const describeConditionGroups = (
  c: Condition,
  names: ShownNames = RAW_NAMES,
  catalogue?: Catalogue
): string | null => {
  const kept = c.flatMap((g) => {
    const terms = usable(groupTerms(g))
    return terms.length > 0
      ? [{ none: isNoneGroup(g), words: termWords(terms, names, catalogue) }]
      : []
  })
  if (kept.length === 0) return null
  return kept
    .map(({ none, words }) => {
      const text = !none
        ? words.join(" or ")
        : words.length === 1
          ? `not ${words[0]}`
          : `none of ${orList(words)}`
      return kept.length > 1 && words.length > 1 ? `(${text})` : text
    })
    .join(" and ")
}

/** The question types whose answer is a number: a number term, and a banner group of ranges. */
export const NUMBER_TYPES: QuestionType[] = ["number", "grid-number"]

/**
 * The paths of the catalogue's number fields (every instance of a number or
 * number grid question, and the number variables of the data), and of its
 * questionnaire's other fields and answers. Built once per catalogue: a new
 * catalogue brings new arrays and is a new object.
 */
const fieldIndexes = new WeakMap<Catalogue, { numbers: Set<string>; others: Set<string> }>()
const fieldsOf = (catalogue: Catalogue): { numbers: Set<string>; others: Set<string> } => {
  const known = fieldIndexes.get(catalogue)
  if (known) return known
  const numbers = new Set<string>()
  const others = new Set<string>()
  const add = (q: CatalogueQuestion): void =>
    q.instances.forEach((i) => {
      if (NUMBER_TYPES.includes(q.type)) numbers.add(i.path)
      else {
        others.add(i.path)
        Object.values(i.optionPaths).forEach((p) => others.add(p))
      }
    })
  catalogue.questions.forEach(add)
  ;(catalogue.dataVariables ?? []).forEach((q) => {
    if (NUMBER_TYPES.includes(q.type)) add(q)
  })
  const index = { numbers, others }
  fieldIndexes.set(catalogue, index)
  return index
}

/**
 * Whether a number term names something known not to be a number field.
 * Once the data is loaded every path is known. Before, only the
 * questionnaire is: a path it does not know may be a variable the
 * preparation makes, and is not judged.
 */
const notNumber = (catalogue: Catalogue, path: string): boolean => {
  const { numbers, others } = fieldsOf(catalogue)
  if (numbers.has(path)) return false
  return catalogue.knownPaths !== null || others.has(path)
}

/**
 * Why a term cannot go into the script, or null when it can. A trailing `>`
 * would merge with the closing brackets; the picker never offers one, but a
 * stored document can hold it.
 */
const termLoss = (catalogue: Catalogue, t: ConditionTerm): string | null => {
  const path = termPath(t)
  if (unsafeText(path) || unsafeRef(path)) return "has a character the engine cannot take"
  if (!inLoadedData(catalogue, path)) return "is not in the loaded data"
  if (!isNumberTerm(t)) return null
  if (notNumber(catalogue, path)) return "is not a number"
  return boundsProblem(t.min, t.max)
}

/** One key per distinct term: an answer by its path, a number term by its path and bounds. */
const termKey = (t: ConditionTerm): string =>
  isNumberTerm(t) ? JSON.stringify([t.path, t.min ?? null, t.max ?? null]) : t

/**
 * Why a condition cannot be applied in full: the first term that cannot go
 * into the script, named in words, and how many more there are. null = every
 * term can. A blank term names nothing and is ignored. A number term must
 * name a number field in the loaded data and have a bound, each bound one a
 * script can write as a plain number (`plainNumber`), the from value no
 * greater than the to value. `owner` names the condition in the
 * sentences: "filter" ("The filter names ...") or "book filter".
 */
export const conditionRefusal = (
  c: Condition,
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES,
  owner = "filter"
): string | null => {
  const seen = new Set<string>()
  const lost = c
    .flatMap(groupTerms)
    .filter((t) => {
      const key = termKey(t)
      if (isBlank(t) || seen.has(key)) return false
      seen.add(key)
      return true
    })
    .flatMap((t) => {
      const why = termLoss(catalogue, t)
      return why ? [{ path: termPath(t), why }] : []
    })
  if (lost.length === 0) return null
  const first = `The ${owner} names ${names.path(lost[0].path)}, which ${lost[0].why}.`
  const more = lost.length - 1
  if (more === 0) return first
  return `${first} The ${owner} has ${more} more ${more === 1 ? "condition" : "conditions"} that cannot be applied.`
}
